
/*
 * RISK IS KEY — Variant Gallery Sync
 * Switches the primary product image to the
 * selected Shopify variant's assigned image.
 *
 * Works with existing:
 * [data-product-gallery]
 * [data-product-variants]
 * [data-option-value]
 * [data-gallery-index]
 */

(function () {
  'use strict';

  function initVariantGallery() {
    const gallery = document.querySelector(
      '[data-product-gallery]'
    );

    const productForm = document.querySelector(
      '.product-form'
    );

    if (!gallery || !productForm) return;

    const variantJSON = productForm.querySelector(
      '[data-product-variants]'
    );

    if (!variantJSON) return;

    let variants = [];

    try {
      variants = JSON.parse(variantJSON.textContent);
    } catch (error) {
      console.error(
        'Risk is Key: Could not load variants.',
        error
      );
      return;
    }

    const primaryItem = gallery.querySelector(
      '.product-gallery__item--primary'
    );

    const primaryImage = primaryItem?.querySelector('img');

    if (!primaryImage) return;

    // Preserve the original image in the gallery.
    const originalImage = {
      src: primaryImage.getAttribute('src'),
      srcset: primaryImage.getAttribute('srcset'),
      sizes: primaryImage.getAttribute('sizes'),
      alt: primaryImage.getAttribute('alt')
    };

    function getSelectedVariant() {
      const selectedOptions = Array.from(
        productForm.querySelectorAll(
          '[data-option-value]:checked'
        )
      )
      .sort(
        (a, b) =>
          Number(a.dataset.position) -
          Number(b.dataset.position)
      )
      .map(input => input.value);

      return variants.find(variant =>
        variant.options.length === selectedOptions.length &&
        variant.options.every(
          (option, index) =>
            option === selectedOptions[index]
        )
      );
    }

    function getVariantImage(variant) {
      if (!variant) return null;

      // Shopify product variant JSON generally
      // includes featured_image when assigned.
      return variant.featured_image || null;
    }

    function findMatchingGalleryImage(image) {
      if (!image) return null;

      const targetId = String(image.id || '');

      const targetSrc = (
        image.src || ''
      ).split('?')[0];

      const images = Array.from(
        gallery.querySelectorAll(
          '.rik-gallery-open img'
        )
      );

      return images.find(img => {
        const mediaId =
          img.closest('[data-media-id]')?.dataset.mediaId;

        if (
          targetId &&
          mediaId &&
          mediaId.includes(targetId)
        ) {
          return true;
        }

        const imgSrc = (
          img.currentSrc ||
          img.getAttribute('src') ||
          ''
        ).split('?')[0];

        const normalize = url =>
          url.replace(
            /_(?:\d+x\d*|\d+x|pico|icon|thumb|small|compact|medium|large|grande|original)(?=\.)/,
            ''
          );

        return Boolean(
          targetSrc &&
          normalize(imgSrc) === normalize(targetSrc)
        );
      }) || null;
    }

    function updatePrimaryImage(variant) {
      const featuredImage = getVariantImage(variant);

      if (!featuredImage || !featuredImage.src) {
        return;
      }

      const matchingImage =
        findMatchingGalleryImage(featuredImage);

      const newSrc = matchingImage
        ? matchingImage.getAttribute('src')
        : featuredImage.src;

      if (!newSrc) return;

      // If the selected image already occupies
      // the primary position, nothing needs changing.
      const currentSrc = primaryImage.getAttribute('src');

      if (currentSrc === newSrc) return;

      // Use a matching gallery image's responsive
      // attributes when available.
      primaryImage.setAttribute('src', newSrc);

      if (matchingImage && matchingImage !== primaryImage) {
        const srcset =
          matchingImage.getAttribute('srcset');

        if (srcset) {
          primaryImage.setAttribute('srcset', srcset);
        } else {
          primaryImage.removeAttribute('srcset');
        }
      } else {
        // Prevent an old srcset from overriding
        // the new selected variant image.
        primaryImage.removeAttribute('srcset');
      }

      primaryImage.setAttribute(
        'alt',
        featuredImage.alt ||
        variant.name ||
        'Risk is Key product'
      );

      // Keep the primary image displayed immediately.
      primaryImage.loading = 'eager';

      gallery.dispatchEvent(
        new CustomEvent('rik:variant-image-changed', {
          bubbles: true,
          detail: {
            variant: variant,
            image: featuredImage
          }
        })
      );
    }

    productForm.addEventListener('change', function (event) {
      if (!event.target.matches('[data-option-value]')) {
        return;
      }

      const variant = getSelectedVariant();

      if (variant) {
        updatePrimaryImage(variant);
      }
    });

    // Match the image on initial page load,
    // including URLs with a selected variant.
    const initialVariant = getSelectedVariant();

    if (initialVariant) {
      updatePrimaryImage(initialVariant);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initVariantGallery
    );
  } else {
    initVariantGallery();
  }
})();
